import {ParamMap} from "@angular/router";
import {WorkEntityKind} from "src/app/enums/WorkEntityKind.enum";
import {WorksTypes} from "src/app/enums/Works.enum";
import {QueryFilters} from "src/app/modules/lists/interfaces/filter.interface";
import {parsePeopleTypeFromTagId} from "src/app/modules/lists/utils/entity-tags.util";
import {CatalogSearchContext} from "src/app/modules/shared/interfaces/CatalogSearch.interface";
import {WorkEntitySuggestion} from "src/app/modules/shared/interfaces/WorkEntitySuggestion.interface";

export function getCatalogSearchFilters(
  initialFilters: QueryFilters,
  entities: WorkEntitySuggestion[],
  type: WorksTypes | null,
  enabledTypes: WorksTypes[]
): QueryFilters {
  const filters = {...initialFilters};
  const fields = [
    [WorkEntityKind.People, "people"],
    [WorkEntityKind.Company, "companies"],
    [WorkEntityKind.Series, "series"],
    [WorkEntityKind.Franchise, "franchises"],
  ] as const;
  for (const [kind, field] of fields) {
    const tags = entities
      .filter((entity) => entity.kind === kind)
      .map((entity) => entity.tagId);
    if (tags.length) filters[field] = tags;
    else delete filters[field];
  }
  const seriesTypes = entities
    .filter((entity) => entity.kind === WorkEntityKind.Series && entity.type)
    .map((entity) => entity.type);
  filters.workType = (type ? [type] : enabledTypes).filter(
    (media) =>
      enabledTypes.includes(media) &&
      (!seriesTypes.length ||
        seriesTypes.some((scope) => String(scope) === media))
  );
  return filters;
}

export function buildCatalogSearchContext(
  paramMap: ParamMap,
  queryParamMap: ParamMap
): CatalogSearchContext {
  const filters: QueryFilters = {};
  for (const key of [
    "people",
    "companies",
    "series",
    "franchises",
    "genres",
  ] as const) {
    const values = queryParamMap
      .getAll(key)
      .map((value) => value.trim())
      .filter(Boolean);
    if (values.length) {
      filters[key] = values;
    }
  }
  const type = Object.values(WorksTypes).find(
    (workType) => workType === queryParamMap.get("workType")
  );
  return {
    query: (queryParamMap.get("query") ?? paramMap.get("term") ?? "").trim(),
    filters,
    entities: [
      ...buildRouteEntities(
        WorkEntityKind.People,
        "people",
        "peopleLabel",
        queryParamMap
      ),
      ...buildRouteEntities(
        WorkEntityKind.Company,
        "companies",
        "companiesLabel",
        queryParamMap
      ),
      ...buildRouteEntities(
        WorkEntityKind.Series,
        "series",
        "seriesLabel",
        queryParamMap,
        type
      ),
      ...buildRouteEntities(
        WorkEntityKind.Franchise,
        "franchises",
        "franchisesLabel",
        queryParamMap
      ),
    ],
    type,
  };
}

function buildRouteEntities(
  kind: WorkEntityKind,
  tagParam: string,
  labelParam: string,
  queryParamMap: ParamMap,
  type?: WorksTypes
): WorkEntitySuggestion[] {
  const labels = queryParamMap.getAll(labelParam);
  return queryParamMap.getAll(tagParam).flatMap((value, index) => {
    const tagId = value.trim();
    if (!tagId) {
      return [];
    }
    return [
      {
        kind,
        tagId,
        name: labels[index]?.trim() || tagId,
        slug: "",
        type,
        peopleType:
          kind === WorkEntityKind.People
            ? parsePeopleTypeFromTagId(tagId)
            : undefined,
      },
    ];
  });
}
